import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ElectionOutcomeDto,
  RecordElectionOutcomeRequest,
} from '@signalvote/shared';
import { authService } from '../../services/authService';
import { apiJson } from './apiFetch';
import { financeQueries, queryKeys } from '../../lib/queryKeys';
import { useCampaignStore } from '../../stores';
import { FINANCE_STALE_TIME } from './queryUtils';

// ADR-076 (#630) — the CURRENT campaign's per-stage election outcomes plus the recording mutation,
// backing the owner/platform-admin recording control. A read (one row per recorded stage, each
// carrying its concurrency token) plus a POST that upserts one stage. Recording is a rare admin
// action, not a high-volume single-row ledger edit, so — like the recurring wind-down mutations —
// the mutation simply invalidates the list on success rather than threading an optimistic token
// through the cache; the fresh token arrives with the refetch.

export type { ElectionOutcomeDto, RecordElectionOutcomeRequest };

/**
 * A rejected recording request. Carries the server error code so the form can tell a genuine
 * cross-actor 409 (another admin/tab recorded this stage first) from any other failure.
 */
export class ElectionOutcomeRecordError extends Error {
  code: string;
  status: number;
  constructor(code: string, status: number, message: string) {
    super(message);
    this.name = 'ElectionOutcomeRecordError';
    this.code = code;
    this.status = status;
  }
}

/**
 * GET /api/finance/election-outcomes/current — the current outcome recorded for each stage of the
 * selected campaign (owner / platform-admin; the server 403s anyone else). Campaign-scoped, so it's
 * gated on a selected campaign.
 */
export function useCampaignElectionOutcomes() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.finance.campaignElectionOutcomes(),
    queryFn: () =>
      apiJson<ElectionOutcomeDto[]>('/api/finance/election-outcomes/current', {
        error: 'Failed to fetch election outcomes',
      }),
    enabled: authService.isAuthenticated() && !!selectedCampaign,
    staleTime: FINANCE_STALE_TIME,
  });
}

/**
 * POST /api/finance/election-outcomes — record what happened at a stage (upsert, one row per stage).
 * A runoff date recorded with WentToRunoff changes the finance settings (and their token), the filing
 * deadlines and the key dates, so success refetches all of finance and the key dates, as a settings
 * save does.
 */
export function useRecordElectionOutcome() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: RecordElectionOutcomeRequest) =>
      apiJson<ElectionOutcomeDto>('/api/finance/election-outcomes', {
        method: 'POST',
        json: request,
        error: (body, response) =>
          new ElectionOutcomeRecordError(
            body.code ?? '',
            response.status,
            body.error ?? 'Failed to record the election outcome',
          ),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries(financeQueries);
      queryClient.invalidateQueries({ queryKey: queryKeys.calendar.keyDates() });
    },
  });
}
